import { LitElement, html, css } from 'lit';
import { createRef, ref as litRef } from 'lit/directives/ref.js';

import { KatapultDialog } from '../katapult-elements/katapult-dialog.js';
import '../katapult-elements/katapult-button.js';
import '../katapult-elements/katapult-icon.js';
import '../katapult-drop-down/katapult-drop-down.js';
import '../user-chip/user-chip.js';
import { pascalCase, snakeCase } from 'change-case';

import '@shoelace-style/shoelace/dist/components/spinner/spinner.js';
import '@shoelace-style/shoelace/dist/components/checkbox/checkbox.js';

import { config } from '../../app-configuration/config.js';
import firebase from 'firebase/compat/app';
import { CallCrossServerIdentityAction } from '../../modules/CallCrossServerIdentityAction.js';

/** @import { TemplateResult } from 'lit' */

class UserServerAccessDialog extends LitElement {
  static styles = css`
    sl-spinner {
      font-size: 1.5rem;
      --indicator-color: var(--secondary-color);
      transition: width 0.25s;
    }
    #errorMessage {
      color: var(--error-color);
      font-weight: bold;
      font-size: 0.9rem;
    }
    .userServerAccessList katapult-icon {
      color: var(--sl-color-neutral-600);
    }
    .userServerAccessList {
      display: flex;
      flex-direction: column;
      gap: var(--sl-spacing-medium);
    }
    .serverAccessList {
      display: flex;
      flex-direction: column;
      gap: var(--sl-spacing-x-small);
      padding: var(--sl-spacing-small);
      background-color: var(--sl-color-neutral-50);
      border-radius: var(--sl-border-radius-large);
      border: 1px solid var(--sl-color-neutral-100);
    }
    .serverLabel {
      display: flex;
      align-items: center;
      gap: var(--sl-spacing-x-small);
      font-weight: var(--sl-font-weight-semibold);
    }
    .companyAccessRow {
      display: flex;
      align-items: center;
      gap: var(--sl-spacing-2x-small);
      padding-left: var(--sl-spacing-medium);
      border-radius: var(--sl-border-radius-medium);
    }
    .companyAccessRow:nth-child(even) {
      background-color: var(--sl-color-neutral-0);
    }
    .companyAccessPermissionsRow {
      display: flex;
      width: 200px;
      font-size: var(--sl-font-size-small);
      font-weight: var(--sl-font-weight-normal);
    }
    .companyAccessPermissionsRow > * {
      display: flex;
      justify-content: center;
      align-items: center;
      flex-grow: 1;
      flex-basis: 0;
    }
    .companyAccessPermissionsRow sl-checkbox::part(label) {
      display: none;
    }
    katapult-button:not([iconOnly]) {
      min-width: 75px;
    }
  `;

  static properties = {
    userId: { type: String },
    companyId: { type: String },
    companyName: { type: String },
    userEmail: { type: String },
    userCompanyPermissions: { type: Object },
    userReadPermission: { type: Boolean },
    userWritePermission: { type: Boolean },
    userAdminPermission: { type: Boolean },
    _companyServerAccessOptions: { type: Array, state: true },
    _companyPermissions: { type: Object, state: true },
    _userServerAccessOptions: { type: Object, state: true },
    _disableAllInputs: { type: Boolean, state: true }
  };

  constructor() {
    super();

    this.userId = '';
    this.companyId = '';
    this.companyName = '';
    this.userEmail = '';
    this.userCompanyPermissions = {};
    this.userReadPermission = false;
    this.userWritePermission = false;
    this.userAdminPermission = false;
    this.userServerAccess = [];

    /** @type {CompanyServerAccessOptions} */
    this._companyServerAccessOptions = [];

    /** @type {CompanyServerAccessResponse} */
    this._companyPermissions = { working_groups: {}, auto_added_for_csi: {} };

    /** @type {UserServerAccessOptions} */
    this._userServerAccessOptions = {};
    this._disableAllInputs = false;
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._detachCompanyFirebaseListeners();
  }

  /**
   * A reference to the dialog element
   * @type {import('lit/directives/ref.js').Ref<KatapultDialog>}
   */
  dialogRef = createRef();

  /**
   * Renders a server access item template.
   * @param {UserServerAccessOption} userServerAccessOption
   * @returns {TemplateResult}
   */
  _renderServerAccessItemTemplate(userServerAccessOption) {
    const { isHomeServer, serverName, companyAccess } = userServerAccessOption;
    return html`
      <div class="serverAccessList">
        <div class="serverLabel">
          <katapult-icon .icon=${isHomeServer ? 'home' : 'dns'} size="20"></katapult-icon>
          <div style="flex-grow: 1;">${serverName}</div>
          <div class="companyAccessPermissionsRow">
            <div>Read</div>
            <div>Write</div>
            <div>Admin</div>
            <div></div>
          </div>
        </div>
        ${Object.values(companyAccess).map((userCompanyAccessOption) =>
          this._renderCompanyAccessItemTemplate(userServerAccessOption, userCompanyAccessOption)
        )}
      </div>
    `;
  }

  /**
   * Renders a company access item template.
   * @param {UserServerAccessOption} userServerAccessOption
   * @param {UserCompanyAccessOption} userCompanyAccessOption
   * @returns {TemplateResult}
   */
  _renderCompanyAccessItemTemplate(userServerAccessOption, userCompanyAccessOption) {
    const { serverId, isHomeServer } = userServerAccessOption;
    const {
      companyId,
      companyName,
      userHasAccess,
      userReadPermission,
      userWritePermission,
      userAdminPermission,
      isRootCompany,
      _readPermissionLoading,
      _writePermissionLoading,
      _companyAdminLoading
    } = userCompanyAccessOption;
    const disabled = isHomeServer === true && isRootCompany === true;
    const checkboxDisabled = disabled || !userHasAccess;
    return html`
      <div class="companyAccessRow">
        <div style="flex-grow: 1;">${companyName}</div>
        <div class="companyAccessPermissionsRow">
          <div>
            ${_readPermissionLoading
              ? html`<sl-spinner></sl-spinner>`
              : html`<sl-checkbox
                  id="readPermission"
                  style="display: flex; align-items: center;"
                  data-server-id=${serverId}
                  data-company-id=${companyId}
                  .checked=${userReadPermission}
                  .disabled=${checkboxDisabled || this._disableAllInputs}
                  @sl-change=${this.userCompanyAccessCheckboxChangeHandler.bind(this)}
                ></sl-checkbox>`}
          </div>
          <div>
            ${_writePermissionLoading
              ? html`<sl-spinner></sl-spinner>`
              : html`<sl-checkbox
                  id="writePermission"
                  style="display: flex; align-items: center;"
                  data-server-id=${serverId}
                  data-company-id=${companyId}
                  .checked=${userWritePermission}
                  .disabled=${checkboxDisabled || this._disableAllInputs}
                  @sl-change=${this.userCompanyAccessCheckboxChangeHandler.bind(this)}
                ></sl-checkbox>`}
          </div>
          <div>
            ${_companyAdminLoading
              ? html`<sl-spinner></sl-spinner>`
              : html`<sl-checkbox
                  id="companyAdmin"
                  style="display: flex; align-items: center;"
                  data-server-id=${serverId}
                  data-company-id=${companyId}
                  .checked=${userAdminPermission}
                  .disabled=${checkboxDisabled || this._disableAllInputs}
                  @sl-change=${this.userCompanyAccessCheckboxChangeHandler.bind(this)}
                ></sl-checkbox>`}
          </div>
          <div>
            ${userHasAccess
              ? html`
                  <sl-tooltip content="Revoke the user's access to this company">
                    <katapult-button
                      id="revokeServerAccess"
                      icon="delete"
                      iconOnly
                      noBorder
                      .disabled=${disabled || this._disableAllInputs}
                      .callback=${() => this.updateServerAccess('remove', serverId, companyId)}
                    ></katapult-button>
                  </sl-tooltip>
                `
              : html`
                  <sl-tooltip content="Grant the user access to this company">
                    <katapult-button
                      id="grantServerAccess"
                      icon="add_circle"
                      iconOnly
                      noBorder
                      .disabled=${disabled || this._disableAllInputs}
                      .callback=${() => this.updateServerAccess('push', serverId, companyId)}
                    ></katapult-button>
                  </sl-tooltip>
                `}
          </div>
        </div>
      </div>
    `;
  }

  render() {
    const { _userServerAccessOptions = [] } = this;

    return html`
      <katapult-dialog ${litRef(this.dialogRef)} .width="${600}" icon="dns" closeButton>
        <span slot="title">User Server Access</span>
        <div style="display: flex; flex-direction: column; gap: var(--sl-spacing-medium);">
          <div style="font-size: var(--sl-font-size-small);">
            Use this dialog to add or remove server access for this user. A user with server access will be able to log into that server and
            their user information will be synced across all servers. When you give a user server access for the first time, their current
            read and write permissions on this server will carry over to the new server. From that point on read and write permission will
            not be synced across servers so you must update them individually.
          </div>
          <div
            style="display: flex; align-items: center; justify-content: center; font-size: var(--sl-font-size-medium); gap: var(--sl-spacing-x-small);"
          >
            <div>Server access for <span style="font-weight: var(--sl-font-weight-semibold);">${this.userEmail}</span></div>
            <user-chip .email=${this.userEmail} hide-name></user-chip>
          </div>
          <div class="userServerAccessList">
            ${Object.values(_userServerAccessOptions)
              .sort((a, b) => (b.isHomeServer ? 1 : 0) - (a.isHomeServer ? 1 : 0))
              .map(this._renderServerAccessItemTemplate.bind(this))}
          </div>
          <div id="errorMessage"></div>
        </div>
        <div slot="buttons" flex grow space-between>
          <katapult-button dialog-dismiss>Done</katapult-button>
        </div>
      </katapult-dialog>
    `;
  }

  shouldUpdate(changedProperties) {
    if (changedProperties.has('userId')) {
      // clear the error message element
      this.clearErrorMessage();
    }

    if (changedProperties.has('companyId')) {
      this._updateCompanyFirebaseListeners();
      if (this.companyId) this._updateCompanyPermissions();
    }

    if (['userCompanyPermissions', '_companyServerAccessOptions', '_companyPermissions'].some((prop) => changedProperties.has(prop))) {
      this._updateUserServerAccessOptions();
    }

    const propertiesToIgnore = ['userId', 'companyId', 'userCompanyPermissions', '_companyServerAccessOptions', '_companyPermissions'];
    const shouldRender = !changedProperties.size || changedProperties.keys().some((key) => !propertiesToIgnore.includes(key));
    return shouldRender;
  }

  /**
   * Updates the Firebase listeners for the company.
   *
   * This method first detaches any existing Firebase listeners for the company,
   * then checks if a companyId is present. If a companyId is present, it sets up
   * new Firebase listeners for the company.
   *
   * @returns {Promise<void>} A promise that resolves when the listeners have been updated.
   */
  async _updateCompanyFirebaseListeners() {
    this._detachCompanyFirebaseListeners();
    if (!this.companyId) return;
    this._setupCompanyFirebaseListeners();
  }

  /**
   * Detaches the server access sharing links listener if it exists.
   */
  _detachCompanyFirebaseListeners() {
    this._detachServerAccessSharingLinksListener?.();
  }

  /**
   * Sets up Firebase listeners for company server access sharing links.
   * @returns {Promise<void>} A promise that resolves when the company firebase listeners have been set up.
   */
  async _setupCompanyFirebaseListeners() {
    const { companyId } = this;
    if (!companyId) throw new Error('Cannot set up company firebase listeners without a company id');

    if (this._detachServerAccessSharingLinksListener != null) throw new Error('Sharing links listener already exists');
    const serverAccessSharingLinksRef = globalThis.FirebaseWorker.ref(`photoheight/company_space/${companyId}/sharing_links/server_access`);
    const serverAccessSharingLinksCallback = serverAccessSharingLinksRef.on('value', (snapshot) => {
      this._companyServerAccessOptions = this.computeCompanyServerAccessOptions(snapshot.val() ?? {});
    });
    this._detachServerAccessSharingLinksListener = () => {
      serverAccessSharingLinksRef.off('value', serverAccessSharingLinksCallback);
      this._detachServerAccessSharingLinksListener = null;
    };
  }

  /** @typedef {import('../../../../functions/source/server_modules/CrossServerIdentityAction.js').CompanyServerAccess} CompanyServerAccessResponse */

  /**
   * Fetches the current user's company's permissions from the central server
   * @returns {Promise<CompanyServerAccessResponse>}
   */
  async _fetchCompanyPermissions() {
    const { companyId } = this;
    if (!companyId) throw new Error('Cannot fetch company server access without a company id');

    // Get the company's server access.
    const serverAccess = await CallCrossServerIdentityAction(firebase.auth(), 'GET_COMPANY_SERVER_ACCESS', { companyId })
      .then((res) => res.json())
      .catch((error) => {
        throw new Error(error.message);
      });

    return serverAccess;
  }

  /**
   * Fetches the current user's company's permissions from the central server
   * @returns {Promise<void>} A promise that resolves when the company permissions have been updated.
   */
  async _updateCompanyPermissions() {
    const companyPermissions = await this._fetchCompanyPermissions();
    this._companyPermissions = companyPermissions;
  }

  /**
   * @typedef UserCompanyAccessOption
   * @property {string} companyId
   * @property {string} companyName
   * @property {boolean} userHasAccess
   * @property {boolean} userReadPermission
   * @property {boolean} userWritePermission
   * @property {boolean} userAdminPermission
   * @property {boolean} [isRootCompany]
   * @property {boolean} [_readPermissionLoading]
   * @property {boolean} [_writePermissionLoading]
   * @property {boolean} [_companyAdminLoading]
   */
  /**
   * @typedef UserServerAccessOption
   * @property {string} serverId
   * @property {string} serverName
   * @property {boolean} [isHomeServer]
   * @property {Object<string, UserCompanyAccessOption>} companyAccess
   */
  /**
   * @typedef {Object<string, UserServerAccessOption>} UserServerAccessOptions
   */
  /**
   * Generates and sets the user's server access options to display in the dialog, based on the user's company permissions along with the user's company's server access.
   */
  _updateUserServerAccessOptions() {
    const { userCompanyPermissions, _companyServerAccessOptions, _companyPermissions } = this;

    /** @type {UserServerAccessOptions} */
    const userServerAccessOptions = {};

    const getServerAccessOptions = (serverId) => {
      return (userServerAccessOptions[serverId] ??= { serverId, serverName: this.getServerName(serverId), companyAccess: {} });
    };

    const getCompanyAccessOptions = (
      serverId,
      companyId,
      {
        companyName = companyId,
        userHasAccess = false,
        userReadPermission = false,
        userWritePermission = false,
        userAdminPermission = false
      } = {}
    ) => {
      const serverAccessOptions = getServerAccessOptions(serverId);
      const existingCompanyAccessOptions = this._userServerAccessOptions?.[serverId]?.companyAccess?.[companyId] ?? {};
      const {
        _readPermissionLoading = false,
        _writePermissionLoading = false,
        _companyAdminLoading = false
      } = existingCompanyAccessOptions;
      return (serverAccessOptions.companyAccess[companyId] ??= {
        companyId,
        companyName,
        userHasAccess,
        userReadPermission,
        userWritePermission,
        userAdminPermission,
        _readPermissionLoading,
        _writePermissionLoading,
        _companyAdminLoading
      });
    };

    if (config.appName === 'katapult-dev' && this.companyId === 'katapult') {
      const excludedServers = [
        'attachment-portal-demo',
        'beta-katapult-pro',
        'fir-pole-applications',
        'lge-ku-pole-applications-dev',
        'training-katapult-pro',
        'testing-katapult-pro'
      ];
      // If we are on the dev server, and the company is 'katapult', we will show all servers
      Object.keys(config.serverNames ?? {}).forEach((serverId) => {
        if (excludedServers.includes(serverId)) return;
        getServerAccessOptions(serverId);
      });
    } else {
      // Add the list of servers the user's company has access to from server sharing links
      _companyServerAccessOptions?.forEach(({ serverId }) => getServerAccessOptions(serverId));
    }

    // Add the user's home server and root company
    const homeServerId = config?.appName;
    const rootCompanyId = this.companyId;
    const rootCompanyName = this.companyName ?? rootCompanyId;
    if (homeServerId) {
      const serverAccessOptions = getServerAccessOptions(homeServerId);
      serverAccessOptions.isHomeServer = true;
      serverAccessOptions.companyAccess[rootCompanyId] = {
        companyId: rootCompanyId,
        companyName: rootCompanyName,
        userHasAccess: true,
        userReadPermission: this.userReadPermission,
        userWritePermission: this.userWritePermission,
        userAdminPermission: this.userAdminPermission,
        isRootCompany: true
      };
    }

    // Add the rootCompany to each server unless the company is in _companyPermissions.auto_added_for_csi
    for (const serverId in userServerAccessOptions) {
      const isCompanyAutoAddedForCSI = _companyPermissions.auto_added_for_csi?.[serverId] === rootCompanyId;
      if (isCompanyAutoAddedForCSI) continue;

      // Get the user's permissions for the root company on this server
      const userHasAccess = !!userCompanyPermissions[serverId]?.[rootCompanyId];
      const {
        read_permission: userReadPermission = false,
        write_permission: userWritePermission = false,
        company_admin: userAdminPermission = false
      } = userCompanyPermissions[serverId]?.[rootCompanyId] ?? {};

      // Get the company access options for the root company on this server
      const companyAccessOptions = getCompanyAccessOptions(serverId, rootCompanyId, { companyName: rootCompanyName });

      // Set the user's server access options
      companyAccessOptions.userHasAccess = userHasAccess;
      companyAccessOptions.userReadPermission = userReadPermission;
      companyAccessOptions.userWritePermission = userWritePermission;
      companyAccessOptions.userAdminPermission = userAdminPermission;
      companyAccessOptions.isRootCompany = true;
    }

    // Add working groups to each server from _companyPermissions.working_groups
    for (const serverId in _companyPermissions.working_groups) {
      const serverWorkingGroups = _companyPermissions.working_groups[serverId];
      for (const companyId in serverWorkingGroups) {
        const { name: companyName } = serverWorkingGroups[companyId];

        // Get the user's permissions for the working group on this server
        const userHasAccess = !!userCompanyPermissions[serverId]?.[companyId];
        const {
          read_permission: userReadPermission = false,
          write_permission: userWritePermission = false,
          company_admin: userAdminPermission = false
        } = userCompanyPermissions[serverId]?.[companyId] ?? {};

        // Get the company access options for the working group on this server
        const companyAccessOptions = getCompanyAccessOptions(serverId, companyId, { companyName });

        // Set the user's server access options
        companyAccessOptions.userHasAccess = userHasAccess;
        companyAccessOptions.userReadPermission = userReadPermission;
        companyAccessOptions.userWritePermission = userWritePermission;
        companyAccessOptions.userAdminPermission = userAdminPermission;
      }
    }

    this._userServerAccessOptions = userServerAccessOptions;
  }

  /** @typedef {{ serverId: string, serverName: string, linkId: string }[]} CompanyServerAccessOptions */
  /**
   * @param {Object[]} serverAccessSharingLinks
   * @returns {CompanyServerAccessOptions}
   */
  computeCompanyServerAccessOptions(serverAccessSharingLinks) {
    const serverAccessOptions = [];

    for (const [key, sharingLink] of Object.entries(serverAccessSharingLinks ?? {})) {
      // Skip expired or pending links.
      if (sharingLink.pending == true || (sharingLink.expires_at != null && sharingLink.expires_at <= Date.now())) continue;
      // Only include links where this server is the recipient.
      if (sharingLink.recipient?.server_id != config?.appName) continue;

      const serverId = sharingLink.sender?.server_id;
      const serverName = this.getServerName(serverId);

      // Check for duplicates
      if (serverAccessOptions.some((option) => option.serverId == serverId)) continue;
      // Add the server to the list of options
      serverAccessOptions.push({ serverId, serverName, linkId: key });
    }

    return serverAccessOptions;
  }

  /**
   * Retrieves the server name corresponding to the given server ID.
   * @param {string} serverId - The ID of the server.
   * @returns {string} The name of the server if found in the configuration; otherwise, returns the server ID.
   */
  getServerName(serverId) {
    return config?.serverNames?.[serverId] ?? serverId;
  }

  /** Open the user server access management dialog */
  open() {
    this.dialogRef.value?.open();
  }

  async userCompanyAccessCheckboxChangeHandler(e) {
    const { currentTarget: checkbox } = e;
    const { checked, dataset } = checkbox;
    const { serverId, companyId } = dataset;

    // Replace the checkbox with a spinner
    this._userServerAccessOptions[serverId].companyAccess[companyId][`_${checkbox.id}Loading`] = true;
    this._userServerAccessOptions = { ...this._userServerAccessOptions };

    const permissionToUpdate = /** @type {UpdateServerAccessAction} */ (snakeCase(checkbox.id));

    await this.updateServerAccess(permissionToUpdate, serverId, companyId, checked).catch(() => {
      // If the call fails, revert the checkbox state
      this._userServerAccessOptions[serverId].companyAccess[companyId][`user${pascalCase(checkbox.id)}`] = !checked;
      this._userServerAccessOptions = { ...this._userServerAccessOptions };
    });

    // Display the checkbox again
    this._userServerAccessOptions[serverId].companyAccess[companyId][`_${checkbox.id}Loading`] = false;
    this._userServerAccessOptions = { ...this._userServerAccessOptions };
  }

  /**
   * @typedef {'push'|'remove'|'read_permission'|'write_permission'|'company_admin'} UpdateServerAccessAction
   */
  /**
   * Updates the server access for a user.
   *
   * @param {UpdateServerAccessAction} action - The action to be performed
   * @param {string} serverId - The ID of the server.
   * @param {string} companyId - The ID of the company.
   * @param {boolean} [newPermission] - The new permission to be set. Defaults to true if not provided. This is only needed for 'read_permission', 'write_permission', and 'company_admin' actions.
   * @returns {Promise<void>} A promise that resolves when the server access has been updated.
   * @throws {Error} Throws an error if the update fails.
   */
  async updateServerAccess(action, serverId, companyId, newPermission) {
    // Disable all inputs
    this._disableAllInputs = true;
    // Create a batch of user access changes
    const batchUserAccessChanges = {
      [serverId]: {
        [companyId]: {
          [action]: newPermission != undefined ? newPermission : true
        }
      }
    };
    // Get the sharing link for the server
    const sharingLinkId = this.getSharingLinkIdForServer(serverId);

    // Apply the user access changes
    await CallCrossServerIdentityAction(firebase.auth(), 'APPLY_USER_ACCESS_CHANGES', {
      companyId: this.companyId,
      userId: this.userId,
      batchUserAccessChanges,
      linkId: sharingLinkId
    })
      .then(() => {
        // Clear the error message
        this.clearErrorMessage();
        // Re-enable inputs
        this._disableAllInputs = false;
      })
      .catch((error) => {
        this.setErrorMessage(error.message);
        this._disableAllInputs = false;
        throw new Error(error.message);
      });
  }

  /**
   * Retrieves the sharing link ID for a given server.
   * @param {string} serverId - The ID of the server for which to retrieve the sharing link ID.
   * @returns {string|undefined} The sharing link ID associated with the specified server, or undefined if not found.
   */
  getSharingLinkIdForServer(serverId) {
    return this._companyServerAccessOptions.find((option) => option.serverId === serverId)?.linkId;
  }

  setErrorMessage(message) {
    /** @type {HTMLElement?} */
    const errorMessageElement = this.renderRoot.querySelector('#errorMessage');
    if (errorMessageElement) errorMessageElement.innerText = message;
  }

  clearErrorMessage() {
    this.setErrorMessage('');
  }
}

window.customElements.define('user-server-access-dialog', UserServerAccessDialog);
